import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { useFormikContext } from 'formik';
import { debounce } from 'lodash';
import { useObserver } from 'mobx-react';
import React, {
  ChangeEvent,
  memo,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { EquipmentTreelistContext } from '..';
import { translate } from '../../../../../../common/intl';
import {
  Autocomplete,
  FormikField,
} from '../../../../../components/forms/fields-next';
import { OptionType } from '../../../../../components/forms/fields/Autocomplete/types';
import OverflowContent from '../../../../../components/OverflowContent';
import { FB } from '../../../../helpers';
import { EditableEquipmentItem } from '../../../types';
import { ADD_FIELD, MIN_SEARCH_TERM_LENGTH } from '../constants';
import { useFetchData } from '../hooks/useFetchData';
import { isEditEquipItem } from '../utils';
import useStyles from './styles';
import TreeListCellWrapper from './TreelistCellWrapper';
import { CustomTreeListCellProps } from './types';

interface EditorProps {
  field?: string
  dataItem: EditableEquipmentItem
  isMPIForm?: boolean
  isEBProcedure?: boolean
}

const Editor: React.FC<EditorProps> = ({
  field = '',
  dataItem,
  isMPIForm,
  isEBProcedure,
}) => {
  const { setFieldValue, errors, getFieldProps, values }
    = useFormikContext<EditableEquipmentItem>();
  const { equipmentOptions, allowedEditEqOptions, releasedEquipmentOptions } = useFetchData();
  const { disabledOptions } = React.useContext(EquipmentTreelistContext);
  const [inputText, setInputText] = useState<string>();
  const [searchText, setSearchText] = useState('');
  const isFreeSolo = values?.LHRTparameter || isEBProcedure;
  const isLoading = !equipmentOptions.length;
  const draftValue = values[field] === undefined ? dataItem[field] : values[field];
  const selectedId = typeof draftValue === 'string' ? draftValue : '';
  const value = useMemo(() => equipmentOptions.find((option) => option.value === selectedId)
    ?? { label: selectedId, value: selectedId }, [equipmentOptions, selectedId]);
  const items = useMemo(() => {
    if (isFreeSolo && searchText.length >= MIN_SEARCH_TERM_LENGTH) {
      return releasedEquipmentOptions.filter((option) => option.label.toLowerCase().includes(searchText.toLowerCase()));
    }
    return isEBProcedure ? allowedEditEqOptions : releasedEquipmentOptions;
  }, [allowedEditEqOptions, isEBProcedure, isFreeSolo, releasedEquipmentOptions, searchText]);
  const updateSearch = useMemo(() => debounce(setSearchText, 1000, { leading: true, trailing: true }), []);

  useEffect(() => () => updateSearch.cancel(), [updateSearch]);

  useEffect(() => {
    if (!isMPIForm) {
      return;
    }

    const fieldValue = values[field] || inputText;

    if (fieldValue && !FB.isUUID(fieldValue)) {
      const updatedValue = isFreeSolo ? fieldValue : '';
      setInputText(undefined);
      updateSearch.cancel();
      setSearchText('');
      setFieldValue(field, updatedValue);
    }
  }, [isFreeSolo]);

  const handleChange = (_: ChangeEvent<unknown>, option?: OptionType) => {
    updateSearch.cancel();
    setSearchText('');
    setInputText(undefined);
    field && setFieldValue(field, option?.value ?? '');
    setFieldValue(ADD_FIELD, dataItem[ADD_FIELD]);
  };

  const onInputChange = (
    _: React.ChangeEvent<{}>,
    inputVal: string,
    reason: string,
  ) => {
    if (reason !== 'input') {
      return;
    }

    setInputText(inputVal);
    setFieldValue(field, isFreeSolo ? inputVal : '');
    if (!inputVal) {
      updateSearch.cancel();
      setSearchText('');
    } else {
      updateSearch(inputVal);
    }
  };

  return (
    <FormikField name={field} required error={Boolean(errors[field])}>
      <Autocomplete
        {...getFieldProps(field)}
        value={value}
        inputValue={inputText ?? value.label}
        forcePopupIcon
        options={items}
        onInputChange={onInputChange}
        disableOpenOnFocus
        blurOnSelect
        ListboxComponent={({ children, ...otherProps }) => {
          return (
            <>
              <ul {...otherProps}>{children}</ul>
            </>
          );
        }}
        getOptionDisabled={(option) =>
          (isEBProcedure && disabledOptions?.includes(option.value)) ?? false
        }
        placeholder={translate('common.select')}
        getOptionLabel={(option: OptionType) => option.label}
        freeSolo
        loading={isLoading}
        onChange={handleChange}
        size="small"
      />
    </FormikField>
  );
};

const IDCell: React.ComponentType<CustomTreeListCellProps> = (props) => {
  const {
    hasChildren,
    dataItem,
    field,
    level,
    onExpandChange,
    expanded,
    getItem,
    isMPIForm,
    isEBProcedure,
  } = props;
  const isEditMode = isEBProcedure
    ? isEditEquipItem(dataItem)
      && !dataItem.equipmentId
      && !dataItem.isActualIdAddition
    : isEditEquipItem(dataItem)
      && !dataItem.actualId
      && !dataItem?.isActualIdAddition;
  const classes = useStyles({ isEditMode });
  const fieldValue = useObserver(() => getItem?.(dataItem[field ?? ''] ?? '') ?? '');

  const onClick = (ev) => {
    onExpandChange(ev, dataItem, level);
  };

  const displayValue = dataItem.type
    ? `${dataItem.id}:${fieldValue}`
    : fieldValue;

  return (
    <TreeListCellWrapper {...props}>
      <OverflowContent title={displayValue} deferred>
        {hasChildren && !isEditMode && (
          <span
            className={cx(
              classes.defaultIcon,
              { [classes.expand]: hasChildren },
              { [classes.disabled]: !hasChildren },
            )}
          >
            <FontAwesomeIcon
              data-cy="minimize-row-button"
              onClick={hasChildren ? onClick : undefined}
              size="sm"
              icon={
                expanded || !hasChildren
                  ? regular('square-minus')
                  : solid('square-plus')
              }
            />
          </span>
        )}
        <span
          className={cx(
            classes.nameCell,
            classes.titleInfoCell,
            hasChildren ? classes.boldText : classes.normalText,
          )}
        >
          {isEditMode && (
            <div className={classes.idCellEditWrapper}>
              <Editor
                key={`${dataItem.id}:${field}`}
                {...{
                  field,
                  dataItem,
                  isMPIForm,
                  isEBProcedure,
                }}
              />
            </div>
          )}
          {!isEditMode && displayValue}
        </span>
      </OverflowContent>
    </TreeListCellWrapper>
  );
};

export default memo(IDCell);
